import {
  type Control,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStockItemsQuery } from '../queries';
import { mapStocksStockItemsToFlatStockItems } from '../entity';
import AutoComplete from '@shared/components/AutoComplete';
import Loader from '@shared/components/Loader';
import { queryClient } from '@shared/queries';

type StockItemsAutoCompleteProps<TFieldValues extends FieldValues> = {
  id?: string;
  fieldPath: Path<TFieldValues>;
  control: Control<TFieldValues>;
  defaultValue?: FieldPathValue<TFieldValues, FieldPath<TFieldValues>>;
  type?: 'MATERIAL' | 'GOODS' | 'BOTH';
};

const StockItemsAutoComplete = <TFieldValues extends FieldValues>(
  props: StockItemsAutoCompleteProps<TFieldValues>,
) => {
  const { id, control, fieldPath, defaultValue, type = 'BOTH' } = props;
  const intl = useIntl();
  const { data: stocksWithStockItems, isLoading, isError } = useStockItemsQuery(undefined);
  const stockItems = mapStocksStockItemsToFlatStockItems(stocksWithStockItems);
  const filteredStockItems = stockItems?.filter(stockItem =>
    type === 'BOTH' ? true : stockItem.itemType === type,
  );

  if (isLoading) {
    return <Loader />;
  }

  if (!filteredStockItems || isError) {
    return (
      <FormattedMessage
        defaultMessage="Skladové položky nebyly nalezeny."
        id="stockItem.notFound"
      />
    );
  }

  const stockItemsOptions = filteredStockItems.map(stockItem => ({
    id: stockItem.id,
    name: stockItem.itemName,
  }));

  return (
    <AutoComplete
      id={id}
      optionsPlacement="top-start"
      options={stockItemsOptions}
      defaultValue={defaultValue}
      control={control}
      getOptionLabel={o => o.name}
      getOptionValue={o => o.id}
      fieldPath={fieldPath}
      label={intl.formatMessage({
        id: 'stockItem.chooseItem',
        defaultMessage: 'Vyberte položku',
      })}
      placeholder={intl.formatMessage({
        id: 'stockItem.search',
        defaultMessage: 'Hledejte...',
      })}
      onChange={() => {
        queryClient.invalidateQueries({ queryKey: ['procedures'] });
      }}
    />
  );
};

export default StockItemsAutoComplete;
